import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import { useTheme } from "../theme";
import ChatMarkdown from "./ChatMarkdown";
import ConfirmModal from "./ConfirmModal";
import GenerateArtifactsDialog from "./GenerateArtifactsDialog";
import Loader from "./Loader";
import { DIALOG_HEIGHT } from "./dialogSize";
import RunVariablesDialog from "./RunVariablesDialog";
import { XrayTestSteps, scenarioCount } from "./TestCaseRow";

/* Single-screen Playwright-script build flow.
 *
 * Entry: the "+ Create Playwright Scripts" button in the Requirement Board's
 * Tests column header. By the time the user is here, Project / Epic / Story
 * are already picked, so none of them appear as inputs — only the fields that
 * truly belong to this step: which Xray tests to automate, which page to
 * connect to, and which model to drive the agent.
 *
 * Everything else that used to live in the old two-step CreateTestsModal +
 * CreateTestCasePage flow — Test Suite / Category dropdowns, AI vs Manual
 * choice, Jira "Load ticket" row, the Xray-context checkbox grid — is gone.
 * The four Xray context types (Epic, Preconditions, Test Set, Test Plan) are
 * now sent to the AI unconditionally in the request body: the backend already
 * supports them, and the UI toggles never had a reason to say "no" here.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Jira → menu matchers, mirrored from CreateTestCasePage so the Connect-to
   dropdown auto-selects the page that the ticket's Module/Page field points
   at (same behaviour the user had before this screen existed). */
function matchMenuByLabel(label, menuOptions) {
  if (!label || !Array.isArray(menuOptions)) return null;
  const target = String(label).trim().toLowerCase();
  if (!target) return null;
  const exact = menuOptions.find(
    (o) => String(o.label || "").trim().toLowerCase() === target,
  );
  if (exact) return exact;
  const contains = menuOptions.find((o) =>
    String(o.label || "").trim().toLowerCase().includes(target),
  );
  if (contains) return contains;
  const reverse = menuOptions.find((o) => {
    const ml = String(o.label || "").trim().toLowerCase();
    return ml.length >= 3 && target.includes(ml);
  });
  if (reverse) return reverse;
  return null;
}

/* The sign-in screen is not a menu page, so a Module/Page of "Sign In" /
   "Login" / "Log on" can never match the menu list — it means the
   "Login Page" option (__login__). Checked only after an exact menu match, so
   a real page such as "Authentication Providers" still wins. */
const LOGIN_MODULE_RE = /^(sign[\s-]?in|log[\s-]?in|log[\s-]?on|sign[\s-]?on)(\s+(page|screen))?$|^login\s*page$/i;
function isLoginModule(label) {
  return LOGIN_MODULE_RE.test(String(label || "").trim());
}

const JIRA_STOPWORDS = new Set([
  "a", "an", "and", "as", "at", "be", "by", "for", "from", "in", "is", "it",
  "of", "on", "or", "that", "the", "this", "to", "we", "with", "add", "new",
  "update", "fix", "bug", "issue", "feature", "user", "users", "page", "screen",
]);
function suggestMenuMatch(ticket, menuOptions) {
  if (!ticket || !Array.isArray(menuOptions) || menuOptions.length === 0) return null;
  const components = (ticket.components || []).map((c) => (c || "").trim()).filter(Boolean);
  const labels = (ticket.labels || []).map((l) => (l || "").trim()).filter(Boolean);
  const summaryWords = new Set(
    ((ticket.summary || "").toLowerCase().match(/[a-z0-9]+/g) || []).filter(
      (w) => w.length >= 3 && !JIRA_STOPWORDS.has(w),
    ),
  );
  const screenHints = labels
    .filter((l) => l.toLowerCase().startsWith("screen:") && l.includes(":"))
    .map((l) => l.split(":", 2)[1].trim().toLowerCase());

  let best = null;
  for (const opt of menuOptions) {
    const label = String(opt.label || "").trim();
    if (!label) continue;
    const labelLc = label.toLowerCase();
    let score = 0;
    const reasons = [];
    for (const comp of components) {
      const cLc = comp.toLowerCase();
      if (cLc === labelLc) {
        score += 3;
        reasons.push(`matched Component '${comp}'`);
      } else if (cLc && (labelLc.includes(cLc) || cLc.includes(labelLc))) {
        score += 2;
        reasons.push(`partial Component match '${comp}'`);
      }
    }
    for (const hint of screenHints) {
      if (hint === labelLc || (hint && labelLc.includes(hint))) {
        score += 2;
        reasons.push(`matched label 'screen:${hint}'`);
      }
    }
    const labelWords = new Set(labelLc.match(/[a-z0-9]+/g) || []);
    const overlap = [...summaryWords].filter((w) => labelWords.has(w));
    if (overlap.length) {
      score += Math.min(overlap.length, 3);
      reasons.push(`summary words: ${overlap.slice(0, 3).sort().join(", ")}`);
    }
    if (score >= 3 && (!best || score > best.confidence)) {
      best = { menuId: opt.id, confidence: score, reason: reasons[0] || "keyword match" };
    }
  }
  return best;
}

/* Small page-connect dropdown, copied in shape from CreateTestCasePage's
   Dropdown — kept inline so this file stays self-contained. */
export function Dropdown({ value, options, onChange, placeholder = "— select —", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);
  const selected = options.find((o) => o.id === value);
  const isEmpty = options.length === 0;

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => String(o.label || "").toLowerCase().includes(q))
    : options;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && !isEmpty && setOpen((o) => !o)}
        disabled={disabled || isEmpty}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className={selected ? "" : "text-slate-400"}>
          {selected ? selected.label : placeholder}
        </span>
        <Icons.ChevronDown
          className={["h-4 w-4 text-slate-400 transition-transform", open ? "rotate-180" : ""].join(" ")}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-card shadow-lg">
          <div className="border-b border-slate-100 p-1.5">
            <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2">
              <Icons.Search className="h-3.5 w-3.5 flex-none text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent py-1.5 text-sm text-slate-900 placeholder-slate-400 outline-none"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-400">No matches</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.id);
                      setOpen(false);
                    }}
                    className={[
                      "flex w-full items-center justify-between px-3 py-2 text-sm transition",
                      opt.id === value
                        ? "bg-blue-50 font-medium text-blue-700"
                        : "text-slate-700 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    {opt.label}
                    {opt.id === value && <Icons.Check className="h-4 w-4" />}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  View context — what is shared with the AI                          */
/* ------------------------------------------------------------------ */
/* Shows the text `POST xray/context` returns: the exact block generation
   sends (xray_service.build_full_context — the selected tests with their
   steps, then the epic / test sets / test plans / preconditions). The text
   is split back into those parts so each reads on its own; "Copy all" copies
   it verbatim. Layout follows HelpDialog. */
/* Each section uses its Jira issue-type icon (which carries its own colour). */
const CONTEXT_SECTIONS = [
  { id: "epic", label: "Epic", icon: Icons.JiraEpic,
    empty: "No epic is linked to this story." },
  { id: "story", label: "Story", icon: Icons.JiraStory },
  { id: "preconditions", label: "Preconditions", icon: Icons.JiraPrecondition,
    empty: "None of the selected tests has a precondition." },
  { id: "tests", label: "Test", icon: Icons.JiraXrayTest,
    empty: "No tests selected." },
  { id: "how_to_test", label: "How To Test", icon: Icons.Book,
    iconClass: "h-5 w-5 flex-none text-slate-500",
    empty: "None of the selected tests has How To Test in Jira." },
];
/* Test plans and test sets are still recognised below, so their text never
   runs into the section before them, but they are not listed. */
const CONTEXT_LABELS = [
  ["epic", /^EPIC\b/],
  ["test_plan", /^TEST PLAN\b/],
  ["test_set", /^TEST SET\b/],
  ["preconditions", /^PRECONDITIONS\b/],
];

function parseAiContext(text) {
  const out = { epic: [], test_plan: [], test_set: [], preconditions: [], tests: [] };
  const [testsPart, extraPart = ""] = String(text || "").split("BACKGROUND CONTEXT SHARED WITH THE AI:");
  /* Tests: drop the "XRAY TESTS SELECTED — …:" header line, one block each. */
  const testsBody = testsPart.trim().replace(/^XRAY TESTS SELECTED[^\n]*\n?/, "").trim();
  if (testsBody) out.tests = testsBody.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  /* Background: blocks start with their label. A description can hold blank
     lines, so a chunk without a label belongs to the block before it. */
  let last = null;
  for (const chunk of extraPart.trim().split(/\n\s*\n/)) {
    const c = chunk.trim();
    if (!c) continue;
    const hit = CONTEXT_LABELS.find(([, re]) => re.test(c));
    if (hit) {
      out[hit[0]].push(c);
      last = hit[0];
    } else if (last) {
      const arr = out[last];
      arr[arr.length - 1] += `\n\n${c}`;
    }
  }
  return out;
}

/* The bullets under a test's "**Preconditions:**" heading in its Jira How To
   Test field (Markdown from jira/how-to-test). Stops at the next bold heading
   ("**Test steps:**"); indented lines continue the bullet above them. */
export function howToTestPreconditions(md) {
  const items = [];
  let inPre = false;
  for (const line of String(md || "").split("\n")) {
    const heading = line.trim().match(/^\*\*([^*]+?)[:.]?\*\*[:.]?\s*(.*)$/);
    if (heading) {
      inPre = /^pre-?conditions?$/i.test(heading[1].trim().replace(/[:.]$/, ""));
      if (inPre && heading[2].trim()) items.push(heading[2].trim());
      continue;
    }
    if (!inPre || !line.trim()) continue;
    const bullet = line.match(/^\s{0,1}[-*•]\s+(.*)$/);
    if (bullet) items.push(bullet[1].trim());
    else if (items.length) items[items.length - 1] += ` ${line.trim()}`;
  }
  return items;
}

/* The Xray Precondition issues from the context text, by test:
   {testKey: ["PRECONDITION KEY — summary\n description…"]}. */
function xrayPreconditionsByTest(blocks) {
  const out = {};
  let test = null;
  const body = (blocks || []).join("\n").replace(/^PRECONDITIONS \(per Xray test\):\n?/, "");
  for (const line of body.split("\n")) {
    const t = line.match(/^- Test (\S+)/);
    const p = line.match(/^\s+• (.*)$/);
    if (t) {
      test = t[1];
      out[test] = out[test] || [];
    } else if (p && test) {
      out[test].push(p[1].trim());
    } else if (line.trim() && test && out[test].length) {
      out[test][out[test].length - 1] += `\n${line.trim()}`;
    }
  }
  return out;
}

/* A card for one selected test: its key and summary as the title. */
function TestCard({ testKey, summary, children }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-card">
      <p className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 text-sm font-semibold text-slate-900">
        <Icons.JiraXrayTest />
        <span className="text-brand-700">{testKey}</span>
        {summary && <span className="min-w-0 truncate font-normal text-slate-600">— {summary}</span>}
      </p>
      <div className="px-4 py-3 text-[14px] leading-relaxed text-slate-700">{children}</div>
    </div>
  );
}

/* One context entry: first line as its title, the rest as-is. */
function ContextEntry({ text }) {
  const [first, ...rest] = String(text).split("\n");
  const body = rest.join("\n").trim();
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-card">
      <p className="border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 text-sm font-semibold text-slate-900">
        {first.replace(/^(EPIC|TEST PLAN|TEST SET)\s+/, "")}
      </p>
      {body ? (
        <pre className="whitespace-pre-wrap break-words px-4 py-3 font-sans text-[14px] leading-relaxed text-slate-700">
          {body}
        </pre>
      ) : (
        <p className="px-4 py-3 text-sm italic text-slate-400">No description.</p>
      )}
    </div>
  );
}

function AiContextDialog({ story, testKeys, options, onClose }) {
  const [text, setText] = useState("");
  /* Each selected test's Jira "How To Test" (Markdown). Its own error, so a
     Jira hiccup there never hides the rest of the context. */
  const [howToTest, setHowToTest] = useState({ items: {}, found: true, error: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState(CONTEXT_SECTIONS[0].id);
  const [copied, setCopied] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const closeRef = useRef(null);
  const detailRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const loadHowToTest = async () => {
      if (!testKeys.length) return { items: {}, found: true, error: "" };
      try {
        const res = await fetch(
          `${API_BASE_URL}jira/how-to-test?keys=${encodeURIComponent(testKeys.join(","))}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
        return { items: body?.data?.items || {}, found: body?.data?.field_found !== false, error: "" };
      } catch (e) {
        return { items: {}, found: true, error: e.message || "Could not load How To Test." };
      }
    };
    (async () => {
      try {
        const [res, htt] = await Promise.all([
          fetch(`${API_BASE_URL}xray/context`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ story_key: story?.key || "", test_keys: testKeys, options }),
          }),
          loadHowToTest(),
        ]);
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        setHowToTest(htt);
        if (!res.ok) setError(body?.detail || `HTTP ${res.status}`);
        else setText((body?.data?.context || "").trim());
      } catch (e) {
        if (!cancelled) setError(`Could not load the context: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadNonce]);

  useEffect(() => {
    if (detailRef.current) detailRef.current.scrollTop = 0;
  }, [activeId]);

  const parsed = useMemo(() => parseAiContext(text), [text]);
  /* Per selected test, in selection order: its summary (from the test block's
     "KEY — summary" line), How To Test, and its preconditions — the How To
     Test bullets plus any linked Xray Precondition issues — so each one shows
     which test it belongs to. */
  const perTest = useMemo(() => {
    const summaries = {};
    for (const block of parsed.tests) {
      const m = block.split("\n")[0].match(/^(\S+)\s+—\s+(.*)$/);
      if (m) summaries[m[1]] = m[2].trim();
    }
    const xrayPre = xrayPreconditionsByTest(parsed.preconditions);
    return testKeys.map((key) => {
      const htt = (howToTest.items[String(key).toUpperCase()] || "").trim();
      return {
        key,
        summary: summaries[key] || "",
        howToTest: htt,
        preconditions: [
          ...howToTestPreconditions(htt).map((t) => ({ source: "How To Test", text: t })),
          ...(xrayPre[key] || []).map((t) => ({ source: "Xray Precondition", text: t })),
        ],
      };
    });
  }, [parsed, howToTest, testKeys]);
  const withPreconditions = perTest.filter((t) => t.preconditions.length);
  const withHowToTest = perTest.filter((t) => t.howToTest);
  const count = (id) =>
    id === "story" ? 1
      : id === "preconditions" ? withPreconditions.reduce((n, t) => n + t.preconditions.length, 0)
        : id === "how_to_test" ? withHowToTest.length
          : (parsed[id] || []).length;
  const active = CONTEXT_SECTIONS.find((s) => s.id === activeId) || CONTEXT_SECTIONS[0];


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close context"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-slate-900/40 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Context Being used to Create Artifact"
        className={`relative z-10 flex ${DIALOG_HEIGHT} w-[min(1100px,92vw)] flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200`}
      >
        {/* Header */}
        <div className="flex flex-none items-center gap-3.5 border-b border-slate-200 px-6 py-5">
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
            <Icons.Layers className="h-6 w-6" />
          </span>
          <h2 className="min-w-0 flex-1 text-xl font-semibold tracking-tight text-slate-900">
            Context Being used to Create Artifact
          </h2>
        </div>

        {loading ? (
          /* The app's one loader — the guide popup, as on the board. It sits
             above dialogs, so the panes stay empty underneath it. */
          <div className="flex-1">
            <Loader.Overlay
              variant="guide"
              label="Loading context…"
              sublabel={`Reading the epic, test plans, preconditions and how to test for ${story?.key || "this story"}`}
              delay={0}
            />
          </div>
        ) : error ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="max-w-md text-sm text-rose-700">{error}</p>
            <button
              type="button"
              onClick={() => setReloadNonce((n) => n + 1)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <Icons.Refresh className="h-4 w-4" /> Retry
            </button>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            {/* Left: the parts of the context */}
            <nav
              aria-label="Context sections"
              className="cq-scroll w-64 flex-none overflow-y-auto border-r border-slate-200 bg-slate-50/60 p-3"
            >
              {CONTEXT_SECTIONS.map((s) => {
                const isActive = s.id === active.id;
                const n = count(s.id);
                const SIcon = s.icon;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveId(s.id)}
                    aria-current={isActive ? "true" : undefined}
                    className={[
                      "mb-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition",
                      isActive
                        ? "bg-white font-semibold text-brand-800 shadow-sm ring-1 ring-brand-600/15"
                        : "text-slate-600 hover:bg-white/70 hover:text-slate-900",
                    ].join(" ")}
                  >
                    <SIcon className={s.iconClass} />
                    <span className="min-w-0 flex-1 text-[14.5px] leading-snug">{s.label}</span>
                    <span
                      className={[
                        "flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold",
                        n ? "bg-brand-50 text-brand-700" : "bg-slate-100 text-slate-400",
                      ].join(" ")}
                    >
                      {n}
                    </span>
                  </button>
                );
              })}
            </nav>

            {/* Right: the selected part */}
            <div ref={detailRef} className="cq-scroll min-w-0 flex-1 overflow-y-auto px-8 py-7">
              <div className="mx-auto max-w-3xl">
                <h3 className="text-2xl font-semibold tracking-tight text-slate-900">{active.label}</h3>
                <div className="mt-5 space-y-4">
                  {active.id === "story" ? (
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-card">
                      <p className="px-4 py-3 text-sm font-semibold text-slate-900">
                        {story?.key} — {story?.summary || "(no summary)"}
                      </p>
                    </div>
                  ) : active.id === "how_to_test" && (howToTest.error || !howToTest.found) ? (
                    <p className="rounded-xl border border-dashed border-rose-200 px-4 py-6 text-center text-sm text-rose-700">
                      {howToTest.error
                        ? `Couldn't read How To Test: ${howToTest.error}`
                        : 'This Jira has no "How To Test" field.'}
                    </p>
                  ) : count(active.id) === 0 ? (
                    <p className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                      {active.empty}
                    </p>
                  ) : active.id === "how_to_test" ? (
                    perTest.map((t) => (
                      <TestCard key={t.key} testKey={t.key} summary={t.summary}>
                        {t.howToTest ? (
                          <ChatMarkdown text={t.howToTest} />
                        ) : (
                          <span className="text-sm italic text-slate-400">How To Test is empty in Jira.</span>
                        )}
                      </TestCard>
                    ))
                  ) : active.id === "preconditions" ? (
                    perTest.map((t) => (
                      <TestCard key={t.key} testKey={t.key} summary={t.summary}>
                        {t.preconditions.length ? (
                          <ul className="space-y-2">
                            {t.preconditions.map((p, i) => (
                              <li key={i} className="flex gap-2.5">
                                <Icons.JiraPrecondition className="mt-0.5" />
                                <p className="min-w-0 flex-1 whitespace-pre-wrap break-words">{p.text}</p>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-sm italic text-slate-400">No precondition on this test.</span>
                        )}
                      </TestCard>
                    ))
                  ) : (
                    parsed[active.id].map((t, i) => <ContextEntry key={i} text={t} />)
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-none items-center justify-end gap-4 border-t border-slate-200 bg-slate-50/60 px-6 py-3.5">
          <div className="flex items-center gap-2">

            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition  hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Background context kinds sent with every generation from this screen. */
const AI_CONTEXT_OPTIONS = ["epic", "test_set", "test_plan", "preconditions"];

/* The number of a Jira key ("PSR-2083" → 2083), for sorting; unknown last. */
export function ticketOrder(key) {
  const m = /-(\d+)$/.exec(String(key || ""));
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

/* The selected tests, split into those whose Artifact Create replaces and
   those that get a new one. The confirmation names both, so its count matches
   what was selected (PSR-2097: 3 selected, it listed only the 2 automated). */
export function splitSelected(tests, selectedIds, isAutomated) {
  const selected = (tests || []).filter((t) => selectedIds.has(t.issue_id));
  return {
    selected,
    replacing: selected.filter((t) => isAutomated(t)),
    fresh: selected.filter((t) => !isAutomated(t)),
  };
}

export default function BuildPlaywrightScriptsPage({ ctx, onCancel, onDone }) {
  const { userkey } = useTheme();
  const story = ctx?.story || null;
  const epicKey = ctx?.epicKey || story?.epic_key || "";
  const epicSummary = ctx?.epicSummary || story?.epic_summary || "";

  /* -------- Form state --------------------------------------------------- */
  const [connectTo, setConnectTo] = useState("");
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsError, setConnectionsError] = useState("");

  /* The Jira ticket snapshot — used to auto-pick connectTo from Module/Page
     (strong signal) or components/summary keywords (weak signal). Same logic
     as CreateTestCasePage's loadJiraTicket. */
  const [jiraData, setJiraData] = useState(null);
  const [jiraAutoPicked, setJiraAutoPicked] = useState(null);

  const [provider, setProvider] = useState(
    () => (process.env.REACT_APP_DEFAULT_PROVIDER || "gemini").toLowerCase(),
  );
  const [modelOptions, setModelOptions] = useState([]);
  const [model, setModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");

  const [autoLogin, setAutoLogin] = useState(true);
  const [aiContext, setAiContext] = useState("");

  /* -------- Xray data ---------------------------------------------------- */
  const [xrayData, setXrayData] = useState(null);
  const [xraySelected, setXraySelected] = useState(() => new Set());
  const [xrayExpanded, setXrayExpanded] = useState(() => new Set());
  const [xrayCoverage, setXrayCoverage] = useState(null);
  /* Starts true when a story is passed: the fetch only begins after the first
     paint, and starting false flashed the empty "no Xray tests" state first. */
  const [xrayLoading, setXrayLoading] = useState(() => Boolean(ctx?.story?.key));
  const [xrayError, setXrayError] = useState("");
  const [contextOpen, setContextOpen] = useState(false);
  /* Generate asks first, saying which Artifacts will be new and which will
     replace an existing one — before anything changes, not after. */
  const [confirmGenerate, setConfirmGenerate] = useState(false);

  /* No Test Suite is picked here: the generated scripts are saved under the
     epic / story they come from (linked to their Xray tests), and that is
     how the board and the Executions screen find them. */

  /* -------- Generation (a dialog on this screen) ------------------------ */
  /* Generate opens GenerateArtifactsDialog: it starts the run, follows the
     server's one-test-at-a-time queue, then offers Save / Cancel. */
  const [genRun, setGenRun] = useState(null); // {request, tests, values} while open
  const [genNotice, setGenNotice] = useState("");
  /* Create, first: the selected tests' variables while their values are
     asked ({tests}), and while they are read. */
  const [createVars, setCreateVars] = useState(null);
  const [valuesLoading, setValuesLoading] = useState(false);
  const generationStarted = Boolean(genRun);

  /* -------- Loaders ----------------------------------------------------- */
  const loadConnections = useCallback(async () => {
    setConnectionsLoading(true);
    setConnectionsError("");
    try {
      const res = await fetch(`${API_BASE_URL}plantops/pages`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      const list = Array.isArray(body?.data) ? body.data : [];
      setConnections(list);
      if (list.length === 0)
        setConnectionsError("The PlantOps menu service returned no pages.");
    } catch (e) {
      setConnections([]);
      setConnectionsError(e.message || "Could not load the app page list.");
    } finally {
      setConnectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  /* Fetch the Jira ticket snapshot once per story — it drives the silent
     Connect-to default (from Module/Page). */
  useEffect(() => {
    if (!story?.key) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const tRes = await fetch(
          `${API_BASE_URL}jira/ticket?key=${encodeURIComponent(story.key)}`,
        );
        const tBody = await tRes.json().catch(() => ({}));
        if (!cancelled && tRes.ok) setJiraData(tBody?.data || null);
      } catch {
        /* non-fatal: Connect-to can still be picked by hand */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [story?.key]);

  /* Once the ticket + menu list are both in, apply the match. Only if the
     user hasn't manually chosen a page yet — a stale re-fetch must not
     overwrite the user's picked value. */
  useEffect(() => {
    if (connectTo) return;
    if (!jiraData) return;
    // The Login Page option needs no menu list, so don't wait for one.
    if (connections.length === 0 && !isLoginModule(jiraData.module_page)) return;
    let picked = null;
    const mp = (jiraData.module_page || "").trim();
    if (mp) {
      const exact = connections.find(
        (o) => String(o.label || "").trim().toLowerCase() === mp.toLowerCase(),
      );
      const strong = exact || (isLoginModule(mp) ? { id: "__login__" } : matchMenuByLabel(mp, connections));
      if (strong) picked = { menuId: strong.id, reason: `Module/Page: ${mp}`, source: "module_page" };
    }
    if (!picked) {
      const scored = suggestMenuMatch(jiraData, connections);
      if (scored) picked = { ...scored, source: "keyword" };
    }
    if (picked) {
      setConnectTo(picked.menuId);
      setJiraAutoPicked(picked);
    }
  }, [jiraData, connections, connectTo]);

  /* Opened for particular tests (For you: "Create Artifact" on one test):
     only those are preselected. Otherwise every test of the story is. */
  const onlyKeys = (ctx?.xrayTestKeys || []).join(",");
  const loadXrayTests = useCallback(async (storyKey) => {
    const q = (storyKey || "").trim();
    if (!q) return;
    setXrayLoading(true);
    setXrayError("");
    setXrayCoverage(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}xray/discover?story=${encodeURIComponent(q)}`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setXrayData(null);
        setXrayError(body?.detail || `HTTP ${res.status}`);
        return;
      }
      /* Ticket number ascending (PSR-2015 before PSR-2083), fixed from the
         first paint, so the list never shifts while the rest loads. */
      const raw = body?.data || null;
      const data = raw && {
        ...raw,
        tests: [...(raw.tests || [])].sort((a, b) => ticketOrder(a.key) - ticketOrder(b.key) || String(a.key).localeCompare(String(b.key))),
      };
      setXrayData(data);
      setXrayExpanded(new Set());
      const tests = data?.tests || [];
      /* Preselect every test by default. Already-automated tests stay
         selectable — an Artifact can be created for a test any number of
         times; the row just shows that it already has one. */
      const only = new Set(onlyKeys ? onlyKeys.split(",") : []);
      setXraySelected(
        new Set(tests.filter((t) => !only.size || only.has(t.key)).map((t) => t.issue_id)),
      );

      /* Which of these already have a Playwright test case locally? Same
         call CreateTestCasePage makes so greyed-out rows stay consistent. */
      if (tests.length > 0) {
        try {
          const covRes = await fetch(`${API_BASE_URL}coverage/xray`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tests: tests.map((t) => ({
                key: t.key,
                summary: t.summary || "",
                steps: t.steps || [],
              })),
            }),
          });
          const covBody = await covRes.json().catch(() => ({}));
          if (covRes.ok && covBody?.data) setXrayCoverage(covBody.data);
        } catch {
          /* non-fatal: we'll just show everything as unknown-coverage */
        }
      }
    } catch (e) {
      setXrayData(null);
      setXrayError(e.message || "Could not fetch tests.");
    } finally {
      setXrayLoading(false);
    }
  }, [onlyKeys]);

  useEffect(() => {
    if (story?.key) loadXrayTests(story.key);
  }, [story?.key, loadXrayTests]);

  /* Models for the current provider. Re-runs on provider toggle, picks the
     provider's default or the first available model. */
  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    setModelsError("");
    setModelOptions([]);
    setModel("");
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}models?provider=${encodeURIComponent(provider)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setModelsError(body?.detail || `Could not load models (HTTP ${res.status})`);
          return;
        }
        const opts = Array.isArray(body?.data?.models) ? body.data.models : [];
        setModelOptions(opts);
        const def = body?.data?.default;
        const defMatch = opts.find((o) => o.id === def);
        setModel(defMatch ? defMatch.id : opts[0]?.id || "");
      } catch (e) {
        if (!cancelled) setModelsError(`Network error loading models: ${e.message}`);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider]);

  /* -------- Already-automated logic ------------------------------------- */
  const automatedKeySet = useMemo(
    () => new Set(xrayCoverage?.automated_keys || []),
    [xrayCoverage],
  );
  const isAlreadyAutomated = useCallback(
    (test) => automatedKeySet.has(test?.key),
    [automatedKeySet],
  );

  const toggleXraySelected = useCallback((issueId) => {
    setXraySelected((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }, []);

  const toggleXrayExpanded = useCallback((issueId) => {
    setXrayExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }, []);

  /* -------- Select-all helpers ------------------------------------------ */
  const selectableTests = useMemo(() => xrayData?.tests || [], [xrayData]);
  const allSelected =
    selectableTests.length > 0 &&
    selectableTests.every((t) => xraySelected.has(t.issue_id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setXraySelected((prev) => {
        const next = new Set(prev);
        selectableTests.forEach((t) => next.delete(t.issue_id));
        return next;
      });
    } else {
      setXraySelected((prev) => {
        const next = new Set(prev);
        selectableTests.forEach((t) => next.add(t.issue_id));
        return next;
      });
    }
  };

  /* -------- Generate ---------------------------------------------------- */
  const selection = connections.find((c) => c.id === connectTo);
  const targetLogin = connectTo === "__login__";
  const canGenerate =
    !generationStarted &&
    xraySelected.size > 0 &&
    Boolean(connectTo) &&
    Boolean(model) &&
    Boolean(targetLogin || selection); // __login__ sentinel doesn't need a menu match

  /* Artifacts are written on the server from the epic's requirement file and
     each test's Cucumber scenario — no browser. The dialog shows the queue. */
  /* Create, first step: the values of the selected tests' {VARIABLES} (user,
     site, address, …). Save keeps them with the Artifacts and Execute starts
     with them. The AI never gets them: the steps keep their {NAME}s.
     Passwords are not asked; they are typed at each Execute. When the tests
     can't be read, or use no variable to ask, the run just starts. */
  const askValues = async () => {
    if (!canGenerate) return;
    const keys = tests.filter((t) => xraySelected.has(t.issue_id)).map((t) => t.key);
    setValuesLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}features/variables`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ epic_key: epicKey, epic_summary: epicSummary, xray_test_keys: keys }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      const list = Array.isArray(body?.data) ? body.data : [];
      /* The trial gate, before anything is written: an Obsolete Test gets no
         Artifact; a Draft / In Review / Needs Update one is written, but its
         trial runs are not evidence. Said here, and again per test. */
      const gateNote = list
        .flatMap((t) => (t.gate ? [t.gate.refused, ...(t.gate.warnings || [])] : []))
        .filter(Boolean)
        .join(" ");
      if (list.some((t) => (t.variables || []).some((v) => !v.secret))) {
        setCreateVars({ tests: list, notice: gateNote });
        return;
      }
      startGenerate({}, gateNote);
    } catch (e) {
      startGenerate({}, `The tests' variables could not be read (${e.message}); Execute asks for every value.`);
    } finally {
      setValuesLoading(false);
    }
  };

  const startGenerate = (values = {}, notice = "") => {
    if (!canGenerate) return;
    const selectedTests = (xrayData?.tests || []).filter((t) =>
      xraySelected.has(t.issue_id),
    );
    const menu_label = targetLogin ? "Login Page" : selection?.label || "";
    const menu_path = targetLogin ? [] : selection?.path || [];

    /* Best-effort: make sure the story's local ticket anchor exists — the
       saved scripts are linked to the story through it. */
    fetch(`${API_BASE_URL}ticket-links`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ticket_key: story.key,
        project_key: ctx?.projectKey || "",
        epic_key: epicKey,
        summary: story.summary || "",
        status: story.status || "",
        issue_type: story.issue_type || "Story",
        priority: story.priority || "",
        url: story.url || "",
      }),
    }).catch(() => {});

    const genConfig = {
      name: `${story.key} — ${story.summary || "Artifacts"}`,
      /* No Test Suite — Inbox Accept files the scripts under the epic /
         story below instead. */
      boardId: null,
      subcategorykey: null,
      /* The requirement the scripts are filed under on Accept: epic → story
         → Xray test. Stored as real rows / columns, not just labels. */
      projectKey: ctx?.projectKey || "",
      epicKey,
      epicSummary,
      storySummary: story.summary || jiraData?.summary || "",
      storyDescription: jiraData?.description_text || "",
      storyStatus: story.status || jiraData?.status || "",
      storyPriority: story.priority || jiraData?.priority || "",
      storyIssueType: story.issue_type || jiraData?.issue_type || "Story",
      userkey: userkey || null,
      sourceMode: "xray",
      jiraTicket: story.key,
      jiraKey: story.key,
      jiraUrl: story.url || "",
      description: "",
      aiContext: aiContext.trim(),
      provider,
      model,
      autoLogin,
      targetLogin,
      connectTo,
      connLabel: menu_label,
      connectToPath: menu_path,
      moduleQueue: [{ menu_key: targetLogin ? "" : connectTo, menu_label, menu_path }],
      xrayTestKeys: selectedTests.map((t) => t.key),
      xrayStoryKey: story.key,
      xrayTests: selectedTests,
      /* Always true: re-creating an Artifact for an already-automated test
         is allowed from this screen, and the server skips those otherwise. */
      xrayAllowDuplicates: true,
      /* All four context kinds, always — no UI toggle. The backend skips any
         that don't exist on this story. "View context" previews the same. */
      xrayContext: AI_CONTEXT_OPTIONS,
    };
    setGenNotice(notice);
    const run = {
      tests: selectedTests.map((t) => ({ key: t.key, summary: t.summary || "" })),
      /* Kept with the Artifacts on Save, never sent to the AI. */
      values,
      request: {
        name: genConfig.name,
        menu_label: menu_label || "Requirement file",
        menu_path,
        source_mode: "xray",
        ticket: story.key,
        xray_test_keys: genConfig.xrayTestKeys,
        xray_story_key: story.key,
        xray_context: AI_CONTEXT_OPTIONS,
        xray_allow_duplicates: true,
        ai_context: genConfig.aiContext,
        provider: provider || "openai",
        model: model || "",
        auto_login: autoLogin !== false,
        target_login: targetLogin,
        ui_config: genConfig,
      },
    };
    /* The request carries no values: the Artifacts keep their {VARIABLES}, so
       one Artifact can run as any user; Execute starts with `values`. */
    setGenRun(run);
  };

  /* ====================================================================== */
  /*  Render                                                                 */
  /* ====================================================================== */

  const hasStory = Boolean(story?.key);
  const tests = xrayData?.tests || [];

  return (
    /* Fixed to the main content box so the card can own its own scroll.
       `h-[calc(100vh-8rem)]` matches MainPage's `<main>` padding math (viewport
       minus the top header `h-16` and the main container `py-8`) — see the
       comment in MainPage.js:383. Using `h-full` here falls back to zero
       because the wrapping `<div>` in <main> is just a `py-8 max-w` layout
       div with no definite height. */
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
      {/* Sticky top bar — Script icon + title + subtitle on the left. */}
      <header className="flex flex-none items-center gap-3 border-b border-slate-200 bg-card px-6 py-3 ">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-600/15">
          <Icons.Script className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold text-slate-900">
            Create Artifact
          </h1>
          {/* <p className="text-xs text-slate-500">
            Generate from this story's Xray tests
          </p> */}
        </div>
      </header>

      {/* Content area — the card inside fills this and holds its own scroll. */}
      <div className="flex min-h-0 flex-1 px-0 py-0 pt-3">
        <div className="flex h-full w-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
        {/* Scroll surface — only this div scrolls. The sticky Cancel +
            Generate footer below stays visible as the user scrolls through
            the Xray tests and the model / context controls. */}
        <div className="cq-scroll flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
        {/* Story context */}
        {hasStory ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Relates To
            </h2>
            <div className="flex flex-col gap-2">
              {epicKey && (
                <div className="flex items-center gap-2">
                  <Icons.JiraEpic />
                  <span className="text-sm font-semibold text-blue-700">{epicKey}</span>
                  {epicSummary && (
                    <span className="truncate text-sm text-slate-700">{epicSummary}</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icons.JiraStory />
                <span className="text-sm font-semibold text-blue-700">{story.key}</span>
                <span className="truncate text-sm text-slate-800">{story.summary || "(no summary)"}</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Empty-state guard: no story in context */}
        {!hasStory && (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-300 bg-card p-10 text-center">
            <Icons.JiraStory muted />
            <p className="text-sm font-semibold text-slate-700">
              No story was passed to this screen.
            </p>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
            >
              Back to board
            </button>
          </div>
        )}

        {/* Empty-state guard: story has no Xray tests */}
        {hasStory && !xrayLoading && !xrayError && tests.length === 0 && (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-300 bg-card p-10 text-center">
            <Icons.JiraStory muted />
            <p className="text-sm font-semibold text-slate-700">
              {story.key} has no tests yet
            </p>
            <p className="max-w-md text-xs text-slate-500">
              Artifacts are generated FROM tests, so there is nothing
              to automate here. Add tests to the story first, then come back.
            </p>
            <button
              type="button"
              onClick={onCancel}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
            >
              <Icons.ArrowLeft className="h-4 w-4" />
              Back to board
            </button>
          </div>
        )}

        {/* Xray tests picker */}
        {hasStory && (xrayLoading || xrayError || tests.length > 0) && (
          <section>
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-slate-800">
                Tests to automate
              </h2>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                {xraySelected.size}/{tests.length}
              </span>
              <div className="ml-auto flex items-center gap-3">
                {tests.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setContextOpen(true)}
                    disabled={xraySelected.size === 0}
                    title={
                      xraySelected.size === 0
                        ? "Select at least one test to see what the AI will receive"
                        : "See the epic, test plans, preconditions and steps shared with the AI"
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icons.Eye className="h-4 w-4" /> View context
                  </button>
                )}
                {selectableTests.length > 0 && (
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600"
                    />
                    Select all
                  </label>
                )}
              </div>
            </div>

            {xrayLoading && (
              /* Same guide popup as the Requirement Board, held until the
                 tests AND their Automated / steps-changed status are in. */
              <Loader.Overlay
                variant="guide"
                label="Loading tests…"
                sublabel={`Fetching ${story.key}'s tests`}
                delay={0}
              />
            )}
            {xrayError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Couldn't read tests for {story.key}: {xrayError}
                <button
                  type="button"
                  onClick={() => loadXrayTests(story.key)}
                  className="ml-2 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 hover:bg-amber-50"
                >
                  <Icons.Refresh className="h-3 w-3" /> Retry
                </button>
              </div>
            )}

            {tests.length > 0 && (
              <ul className="space-y-2">
                {tests.map((t) => {
                  const selected = xraySelected.has(t.issue_id);
                  const expanded = xrayExpanded.has(t.issue_id);
                  const cov = xrayCoverage?.items?.[t.key] || null;
                  const alreadyDone = isAlreadyAutomated(t);
                  const steps = Array.isArray(t.steps) ? t.steps : [];
                  return (
                    <li
                      key={t.issue_id}
                      className={[
                        "overflow-hidden rounded-xl border bg-card shadow-sm transition",
                        /* Green marks a test that already has an Artifact;
                           blue is a selected test without one. */
                        alreadyDone
                          ? selected
                            ? "border-emerald-400 ring-1 ring-emerald-200"
                            : "border-emerald-300 hover:border-emerald-400"
                          : selected
                            ? "border-blue-300 ring-1 ring-blue-200"
                            : "border-slate-200 hover:border-blue-200",
                      ].join(" ")}
                    >
                      <div className="flex items-center gap-2.5 px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleXraySelected(t.issue_id)}
                          className="h-4 w-4 flex-none rounded border-slate-300 text-blue-600"
                        />
                        <button
                          type="button"
                          onClick={() => toggleXrayExpanded(t.issue_id)}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          <span className="text-sm font-semibold text-blue-700">
                            {t.key}
                          </span>
                          <span
                            className={[
                              "min-w-0 flex-1 truncate text-sm font-medium text-slate-800",
                            ].join(" ")}
                          >
                            {t.summary || "(no summary)"}
                          </span>
                          {alreadyDone && (
                            <span
                              className="flex-none rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-600/20"
                              title={`${t.key} is already automated. Creating its Artifact again replaces the existing one.`}
                            >
                              <Icons.Check className="mr-0.5 inline h-3.5 w-3.5 align-[-2px]" />
                              Automated
                            </span>
                          )}
                          {cov?.drifted && (
                            <span className="flex-none rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-600/20">
                              steps changed
                            </span>
                          )}
                          <span className="flex-none rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                            {scenarioCount(steps)} step{scenarioCount(steps) === 1 ? "" : "s"}
                          </span>
                          <Icons.ChevronDown
                            className={[
                              "h-4 w-4 flex-none text-slate-400 transition-transform",
                              expanded ? "rotate-180" : "",
                            ].join(" ")}
                          />
                        </button>
                      </div>
                      {expanded && (
                        <div className="border-t border-slate-100">
                          {/* The Cucumber scenario, or "No Cucumber scenario…". */}
                          <XrayTestSteps steps={steps} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* Connect to */}
        {hasStory && tests.length > 0 && (
          <section>
            <label className="mb-1 block text-sm font-semibold text-slate-800">
              Connect to <span className="text-rose-500">*</span>
            </label>
            <div className="max-w-md">
              <Dropdown
                value={connectTo}
                options={[
                  { id: "__login__", label: "Login Page — test the sign-in screen", path: [] },
                  ...connections,
                ]}
                onChange={setConnectTo}
                placeholder={
                  connectionsLoading
                    ? "Loading options…"
                    : connectionsError
                      ? "Unavailable"
                      : "— select a page —"
                }
                disabled={connectionsLoading || Boolean(connectionsError)}
              />
              {connectionsError && (
                <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
                  <p className="flex items-start gap-1.5 text-xs leading-snug text-amber-800">
                    <span>{connectionsError}</span>
                  </p>
                  <button
                    type="button"
                    onClick={loadConnections}
                    disabled={connectionsLoading}
                    className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition hover:bg-amber-50 disabled:opacity-60"
                  >
                    <Icons.Refresh className={["h-3 w-3", connectionsLoading ? "animate-spin" : ""].join(" ")} />
                    Retry
                  </button>
                </div>
              )}
              {/* "Picked from Jira" indicator — same affordance CreateTestCasePage
                  has, so the user knows the dropdown value isn't random. */}
              {jiraAutoPicked && connectTo === jiraAutoPicked.menuId && (
                <p className="mt-1 text-xs text-slate-500">
                  <Icons.Spark className="mr-1 inline-block h-3 w-3 text-blue-600" />
                  Auto-picked from Jira — {jiraAutoPicked.reason}
                </p>
              )}
            </div>
          </section>
        )}

        {/* AI model */}
        {hasStory && tests.length > 0 && (
          <section>
            <label className="mb-1 block text-sm font-semibold text-slate-800">
              AI model <span className="text-rose-500">*</span>
            </label>
            <p className="mb-2 text-xs text-slate-500">
              Choose which provider and model author the test cases.
            </p>
            <div className="flex max-w-md flex-wrap items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                {["openai", "gemini"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    className={[
                      "px-3 py-1.5 text-sm font-semibold transition",
                      provider === p
                        ? "bg-blue-600 text-white"
                        : "text-slate-600 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    {p === "openai" ? "OpenAI" : "Gemini"}
                  </button>
                ))}
              </div>
              <div className="min-w-[14rem] flex-1">
                <Dropdown
                  value={model}
                  options={modelOptions.map((o) => ({ id: o.id, label: o.label || o.id }))}
                  onChange={setModel}
                  placeholder={
                    modelsLoading
                      ? "Loading models…"
                      : modelsError
                        ? "Unavailable"
                        : "— select a model —"
                  }
                  disabled={modelsLoading || Boolean(modelsError)}
                />
              </div>
            </div>
            {modelsError && (
              <p className="mt-1 text-xs text-amber-700">{modelsError}</p>
            )}
          </section>
        )}

        {/* AI context */}
        {/* {hasStory && tests.length > 0 && (
          <section>
            <label className="mb-1 block text-sm font-semibold text-slate-800">
              <Icons.Spark className="mr-1 inline-block h-3.5 w-3.5 text-brand-600" />
              Exclusive context for the AI
            </label>
            <p className="mb-2 text-xs text-slate-500">
              Extra guidance only the AI will use when generating and running steps
              (constraints, test data, environment notes, edge cases).
            </p>
            <textarea
              value={aiContext}
              onChange={(e) => setAiContext(e.target.value)}
              placeholder="e.g. Use the staging environment. Test account: qa@example.com. Focus on error handling for expired tokens…"
              rows={4}
              className="w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/25"
            />
          </section>
        )} */}

        </div>
        {genNotice && (
          <div className="flex flex-none items-center gap-2 border-t border-slate-200 bg-slate-50 px-6 py-2 text-xs text-slate-600">
            <Icons.Alert className="h-3.5 w-3.5 flex-none text-slate-400" />
            {genNotice}
          </div>
        )}
        {/* Sticky action bar — stays pinned to the bottom of the card as the
            scroll surface above moves. Holds Auto login + Cancel + Generate
            so the primary action is always one click away, no matter how
            many Xray tests are listed. */}
        {hasStory && tests.length > 0 && (
          <div className="flex flex-none flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/70 px-6 py-3">
            <div className="flex flex-wrap items-center gap-5">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={autoLogin}
                  onChange={(e) => setAutoLogin(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                Auto login
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!canGenerate) return;
                  /* Ask only when a selected test already has an Artifact
                     that generating would replace; otherwise just go. */
                  const replacing = tests.some(
                    (t) => xraySelected.has(t.issue_id) && isAlreadyAutomated(t),
                  );
                  if (replacing) setConfirmGenerate(true);
                  else askValues();
                }}
                disabled={!canGenerate}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
              >
                <Icons.Spark className="h-4 w-4" />
                Create
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {confirmGenerate && (() => {
        const { selected, replacing, fresh } = splitSelected(tests, xraySelected, isAlreadyAutomated);
        const one = replacing.length === 1;
        const testRow = (t) => (
          <li key={t.issue_id} className="flex items-start gap-3 px-3.5 py-2.5">
            <span className="flex-none font-semibold text-blue-700">{t.key}</span>
            <span className="min-w-0 text-slate-700">{t.summary || "(no summary)"}</span>
          </li>
        );
        return (
          <ConfirmModal
            title="Confirmation"
            tone="primary"
            icon={Icons.Spark}
            confirmLabel="OK"
            size="lg"
            onCancel={() => setConfirmGenerate(false)}
            onConfirm={() => {
              setConfirmGenerate(false);
              askValues();
            }}
            message={
              <>
                <p>
                  Are you sure? You selected {selected.length} test{selected.length === 1 ? "" : "s"}.{" "}
                  {replacing.length} already {one ? "has" : "have"} an automated Artifact:
                </p>
                <ul className="cq-scroll mt-3 max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/60">
                  {replacing.map(testRow)}
                </ul>
                <p className="mt-3">
                  Creating again will replace {one ? "its" : "their"} existing automated
                  Artifact{one ? "" : "s"} when you save the created scripts.
                </p>
                {fresh.length > 0 && (
                  <>
                    <p className="mt-3">
                      {fresh.length === 1 ? "This test gets" : `These ${fresh.length} tests get`} a new
                      Artifact:
                    </p>
                    <ul className="cq-scroll mt-3 max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/60">
                      {fresh.map(testRow)}
                    </ul>
                  </>
                )}
              </>
            }
          />
        );
      })()}

      {valuesLoading && (
        <Loader.Overlay
          variant="guide"
          label="Reading the tests' variables…"
          sublabel="Reading each selected test's scenario, How To Test and preconditions, and the values kept with its Artifact"
          delay={0}
        />
      )}

      {createVars && (
        <RunVariablesDialog
          mode="create"
          title="Values for these tests"
          tests={createVars.tests}
          userkey={userkey || null}
          onExecute={(values) => {
            const { notice } = createVars;
            setCreateVars(null);
            startGenerate(values, notice || "");
          }}
          onCancel={() => setCreateVars(null)}
        />
      )}

      {genRun && (
        <GenerateArtifactsDialog
          request={genRun.request}
          tests={genRun.tests}
          variableValues={genRun.values}
          userkey={userkey || null}
          onClose={(notice) => {
            setGenRun(null);
            setGenNotice(notice || "");
          }}
          onSaved={(result) => {
            setGenRun(null);
            onDone?.({ epicKey, storyKey: story?.key || "", ...result });
          }}
        />
      )}

      {contextOpen && (
        <AiContextDialog
          story={story}
          testKeys={tests.filter((t) => xraySelected.has(t.issue_id)).map((t) => t.key)}
          options={AI_CONTEXT_OPTIONS}
          onClose={() => setContextOpen(false)}
        />
      )}
    </div>
  );
}
